/**
 * securityFormHelpers — shared form-state shape + per-engine builders
 * for the SecurityEditFormBody (Slice F1).
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  LoginChange,
  LoginDef,
  RoleChange,
  RoleDef,
  UserChange,
  UserDef,
} from '../../../../sql-resolver/types/ddlEdit';
import type { SecurityKind } from '../../inline/parseParkedSecurityStatement';

export type SecurityEditMode = 'create' | 'alter';

export interface SecurityFormValues {
  /** Principal kind. */
  readonly kind: SecurityKind;
  /** Principal name (logins / users / roles). */
  readonly name: string;
  /** Quote the principal name in the emitted SQL. */
  readonly quoted: boolean;
  /** mysql `'name'@'host'` host part. */
  readonly mysqlHost: string;
  /** Plain-text password (mssql / mysql / oracle / pgsql role-with-login). */
  readonly password: string;
  /** mssql LOGIN: use Windows authentication instead of SQL password. */
  readonly mssqlFromWindows: boolean;
  /** mssql LOGIN: default database. */
  readonly mssqlDefaultDatabase: string;
  /** mssql USER: maps to existing login (FOR LOGIN). */
  readonly mssqlForLogin: string;
  /** mssql USER: WITHOUT LOGIN flag. */
  readonly mssqlWithoutLogin: boolean;
  /** mssql USER: default schema. */
  readonly mssqlDefaultSchema: string;
  /** mssql ROLE: AUTHORIZATION owner. */
  readonly mssqlAuthorization: string;
  /** pgsql ROLE: emit LOGIN attribute (login-capable role). */
  readonly pgsqlLogin: boolean;
  /** pgsql ROLE: SUPERUSER attribute. */
  readonly pgsqlSuperuser: boolean;
  /** oracle USER: IDENTIFIED EXTERNALLY (OS-authenticated). */
  readonly oracleIdentifiedExternally: boolean;
  /** oracle USER: account locked / unlocked. */
  readonly oracleAccountLocked: boolean;
  /** Empty string represents "do not toggle"; 'lock' / 'unlock' set the flag. */
  readonly oracleAccountState: '' | 'lock' | 'unlock';
  /** Active alter action (free-form selector). */
  readonly alterAction: AlterActionId;
  /** New name (alter rename / alter login WITH NAME). */
  readonly alterRenameTo: string;
  /** Alter ROLE — member to add (mssql). */
  readonly alterAddMember: string;
  /** Alter ROLE — member to drop (mssql). */
  readonly alterDropMember: string;
}

export type AlterActionId =
  | 'rotate-password'
  | 'rename'
  | 'add-member'
  | 'drop-member'
  | 'lock'
  | 'unlock'
  | 'enable'
  | 'disable';

export const DEFAULT_SECURITY_FORM_VALUES: SecurityFormValues = {
  kind: 'user',
  name: '',
  quoted: false,
  mysqlHost: '',
  password: '',
  mssqlFromWindows: false,
  mssqlDefaultDatabase: '',
  mssqlForLogin: '',
  mssqlWithoutLogin: false,
  mssqlDefaultSchema: '',
  mssqlAuthorization: '',
  pgsqlLogin: false,
  pgsqlSuperuser: false,
  oracleIdentifiedExternally: false,
  oracleAccountLocked: false,
  oracleAccountState: '',
  alterAction: 'rotate-password',
  alterRenameTo: '',
  alterAddMember: '',
  alterDropMember: '',
};

export function buildLoginDef(values: SecurityFormValues): LoginDef {
  return {
    name: values.name.trim(),
    ...(values.quoted ? { quoted: true } : {}),
    ...(values.mssqlFromWindows
      ? { mssqlFromWindows: true }
      : values.password
        ? { mssqlPassword: values.password }
        : {}),
    ...(values.mssqlDefaultDatabase
      ? { mssqlDefaultDatabase: values.mssqlDefaultDatabase }
      : {}),
  };
}

export function buildUserDef(
  engine: SqlEngine,
  values: SecurityFormValues,
): UserDef {
  if (engine === 'mssql') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.mssqlForLogin ? { mssqlForLogin: values.mssqlForLogin } : {}),
      ...(values.mssqlWithoutLogin ? { mssqlWithoutLogin: true } : {}),
      ...(values.mssqlDefaultSchema
        ? { mssqlDefaultSchema: values.mssqlDefaultSchema }
        : {}),
    };
  }
  if (engine === 'mysql') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.mysqlHost ? { mysqlHost: values.mysqlHost } : {}),
      ...(values.password ? { mysqlIdentifiedBy: values.password } : {}),
    };
  }
  if (engine === 'oracle') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.oracleIdentifiedExternally
        ? { oracleIdentifiedExternally: true }
        : values.password
          ? { oracleIdentifiedBy: values.password }
          : {}),
      ...(values.oracleAccountState === 'lock'
        ? { oracleAccountLocked: true }
        : values.oracleAccountState === 'unlock'
          ? { oracleAccountLocked: false }
          : {}),
    };
  }
  return { name: values.name.trim() };
}

export function buildRoleDef(
  engine: SqlEngine,
  values: SecurityFormValues,
): RoleDef {
  if (engine === 'mssql') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.mssqlAuthorization
        ? { mssqlAuthorization: values.mssqlAuthorization }
        : {}),
    };
  }
  if (engine === 'postgres') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.pgsqlLogin ? { pgsqlLogin: true } : {}),
      ...(values.pgsqlLogin && values.password
        ? { pgsqlPassword: values.password }
        : {}),
      ...(values.pgsqlSuperuser ? { pgsqlSuperuser: true } : {}),
    };
  }
  if (engine === 'mysql') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.mysqlHost ? { mysqlHost: values.mysqlHost } : {}),
    };
  }
  if (engine === 'oracle') {
    return {
      name: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(values.password ? { oracleIdentifiedBy: values.password } : {}),
    };
  }
  return { name: values.name.trim() };
}

export function buildLoginChange(values: SecurityFormValues): LoginChange {
  if (values.alterAction === 'rotate-password') {
    return { mssqlPassword: values.password };
  }
  if (values.alterAction === 'rename') {
    return { mssqlName: values.alterRenameTo };
  }
  if (values.alterAction === 'enable') return { mssqlEnable: true };
  if (values.alterAction === 'disable') return { mssqlEnable: false };
  return {};
}

export function buildUserChange(
  engine: SqlEngine,
  values: SecurityFormValues,
): UserChange {
  if (engine === 'mssql') {
    if (values.alterAction === 'rename') {
      return { mssqlName: values.alterRenameTo };
    }
    if (values.mssqlDefaultSchema) {
      return { mssqlDefaultSchema: values.mssqlDefaultSchema };
    }
    return {};
  }
  if (engine === 'mysql') {
    if (values.alterAction === 'rotate-password') {
      return { mysqlIdentifiedBy: values.password };
    }
    return {};
  }
  if (engine === 'oracle') {
    if (values.alterAction === 'rotate-password') {
      return { oracleIdentifiedBy: values.password };
    }
    if (values.alterAction === 'lock') return { oracleAccountLocked: true };
    if (values.alterAction === 'unlock') return { oracleAccountLocked: false };
    return {};
  }
  return {};
}

export function buildRoleChange(
  engine: SqlEngine,
  values: SecurityFormValues,
): RoleChange {
  if (engine === 'mssql') {
    if (values.alterAction === 'add-member') {
      return { mssqlAddMember: values.alterAddMember };
    }
    if (values.alterAction === 'drop-member') {
      return { mssqlDropMember: values.alterDropMember };
    }
    if (values.alterAction === 'rename') {
      return { mssqlName: values.alterRenameTo };
    }
    return {};
  }
  if (engine === 'postgres') {
    if (values.alterAction === 'rename') {
      return { pgsqlRenameTo: values.alterRenameTo };
    }
    if (values.alterAction === 'rotate-password') {
      return { pgsqlPassword: values.password };
    }
    return {};
  }
  if (engine === 'oracle') {
    if (values.alterAction === 'rotate-password') {
      return { oracleIdentifiedBy: values.password };
    }
    return {};
  }
  return {};
}

export function valuesFromParsed(
  parsed: { kind: SecurityKind; name: string; quoted: boolean; mysqlHost?: string },
): Partial<SecurityFormValues> {
  return {
    kind: parsed.kind,
    name: parsed.name,
    quoted: parsed.quoted,
    ...(parsed.mysqlHost !== undefined ? { mysqlHost: parsed.mysqlHost } : {}),
  };
}

/**
 * Per-engine + per-kind ALTER actions. Keep in sync with the detail-input
 * conditionals + build{Login,User,Role}Change branches. Lives here (not in the
 * component) so the normalization below is unit-testable.
 */
export function getAlterActions(
  engine: SqlEngine,
  kind: SecurityKind,
): readonly AlterActionId[] {
  if (engine === 'mssql' && kind === 'login') {
    return ['rotate-password', 'rename', 'enable', 'disable'];
  }
  if (engine === 'mssql' && kind === 'user') return ['rename'];
  if (engine === 'mssql' && kind === 'role') return ['add-member', 'drop-member', 'rename'];
  if (engine === 'postgres' && kind === 'role') return ['rename', 'rotate-password'];
  if (engine === 'mysql' && kind === 'user') return ['rotate-password'];
  if (engine === 'oracle' && kind === 'user') return ['rotate-password', 'lock', 'unlock'];
  if (engine === 'oracle' && kind === 'role') return ['rotate-password'];
  return [];
}

/**
 * Normalize a possibly-stale alterAction for the current (engine, kind).
 *
 * The form seeds `alterAction` from the GLOBAL default ('rotate-password'),
 * which is invalid for combos like mssql/role (only add/drop-member + rename).
 * When the current action isn't legal for the active actions list we fall back
 * to the first available action so a detail input always renders; a deliberate
 * selection that IS still valid is preserved untouched.
 */
export function normalizeAlterAction(
  actions: readonly AlterActionId[],
  current: AlterActionId,
): AlterActionId | undefined {
  if (actions.length === 0) return undefined;
  return actions.includes(current) ? current : actions[0];
}

/** Per-engine + per-kind support gate for the form's mode/kind combo. */
export function isKindSupportedForEngine(
  engine: SqlEngine,
  kind: SecurityKind,
): boolean {
  if (engine === 'sqlite') return false;
  if (engine === 'postgres') return kind === 'role';
  if (engine === 'mysql') return kind === 'user' || kind === 'role';
  if (engine === 'oracle') return kind === 'user' || kind === 'role';
  if (engine === 'mssql') return true;
  return false;
}

export function getKindUnsupportedTooltip(
  engine: SqlEngine,
  kind: SecurityKind,
): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  if (engine === 'postgres' && kind === 'login') {
    return 'PostgreSQL has no LOGIN — use ROLE with the LOGIN attribute set.';
  }
  if (engine === 'postgres' && kind === 'user') {
    return 'PostgreSQL collapses USER into ROLE — use ROLE with the LOGIN attribute set.';
  }
  if ((engine === 'mysql' || engine === 'oracle') && kind === 'login') {
    return `${engine} has no server-level LOGIN — use USER instead.`;
  }
  return '';
}
